import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Car, Users, Building2, Briefcase, ShoppingBag, MessageSquareWarning,
  AlertCircle, Lightbulb, TrendingUp,
} from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const scenarios = [
  {
    icon: Car,
    category: 'Bilservice & Däckverkstad',
    problem: 'Säsongstoppar (vår/höst) skapar kö — hundratals bokningsförfrågningar per dag, personal jobbar övertid och kunder får vänta i timmar.',
    solution: 'AI-bokningsagent som svarar direkt via webb, chat och email. Hanterar bokning, bekräftelse och påminnelse automatiskt — dygnet runt utan mänsklig inblandning.',
    effect: 'Svarstid minskar från timmar till sekunder. Personal frigörs 10–15 timmar/vecka. Inga missade bokningar.',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.25)',
    tag: 'AI-Automation',
    tagColor: 'text-amber-700 bg-amber-50',
  },
  {
    icon: Users,
    category: 'Idrottsförening',
    problem: 'Kassören hanterar manuella inbetalningar, påminnelser och kvittenser via Swish. Dessutom kräver events och kiosker separata lösningar.',
    solution: 'Swishkassa — en enkel betalningslösning för föreningskassor, events och kiosker. Automatiska kvittenser, realtidsöversikt och ingen dyr kortterminal.',
    effect: 'Kassören sparar 5–8 timmar per månad. Ekonomin blir transparent och alla ser försäljning i realtid.',
    gradient: 'from-emerald-400 to-teal-500',
    glow: 'rgba(52,211,153,0.25)',
    tag: 'Swishkassa',
    tagColor: 'text-emerald-700 bg-emerald-50',
  },
  {
    icon: Building2,
    category: 'Fastighetsbolag',
    problem: 'Hyresgäster ringer och mejlar om allt — felanmälningar, frågor om hyra, nycklarbyte. Samma frågor besvaras av personal om och om igen.',
    solution: 'AI-chatbot på webbplatsen som svarar på vanliga frågor, tar emot felanmälningar och guidar hyresgäster rätt — utan att blanda in personal.',
    effect: 'Färre samtal till kundtjänst — svarstid från dagar till sekunder. Personal hanterar bara ärenden som kräver mänskligt omdöme.',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
    tag: 'AI-Chatbot',
    tagColor: 'text-indigo-700 bg-indigo-50',
  },
  {
    icon: Briefcase,
    category: 'Konsultbolag / Byrå',
    problem: 'Interna processer som tidrapportering, projektrapporter och onboarding av nya kunder görs manuellt. Det tar tid och är känsligt för misstag.',
    solution: 'Workflow-automation som kopplar ihop befintliga system (CRM, e-post, kalkylark) och automatiserar repetitiva flöden utan att byta ut något.',
    effect: '15–20 timmar repetitivt arbete försvinner ur veckan. Rätt information hamnar på rätt ställe automatiskt — inga manuella fel.',
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.25)',
    tag: 'AI-Automation',
    tagColor: 'text-violet-700 bg-violet-50',
  },
  {
    icon: ShoppingBag,
    category: 'E-handel & Orderhantering',
    problem: 'Orderbekräftelser, returer och lagerstatusuppdateringar hanteras manuellt. Kunder hör av sig för att fråga om status — varje dag.',
    solution: 'Automatiserat orderflöde: bekräftelser, statusuppdateringar och returer sköts utan manuell handpåläggning. AI-chatbot svarar på produktfrågor dygnet runt.',
    effect: 'Eliminerar 20+ timmar manuellt arbete per vecka. Kundservice-ärenden minskar med 40–60 % — utan extra personal.',
    gradient: 'from-pink-500 to-rose-500',
    glow: 'rgba(236,72,153,0.25)',
    tag: 'AI-Automation',
    tagColor: 'text-pink-700 bg-pink-50',
  },
  {
    icon: MessageSquareWarning,
    category: 'Privatperson med konsumentärende',
    problem: 'Du har fått en felaktig faktura, ett trasigt köp eller dålig service — men vet inte hur du formulerar ett klagomål som tas på allvar.',
    solution: 'Klagobot guidar dig genom ärendet och skriver ett professionellt klagomålsbrev åt dig — baserat på konsumentlagstiftning och bästa praxis.',
    effect: 'Färdigt brev på 2–5 minuter istället för timmar. Baserat på konsumentlagstiftning — ger faktiskt resultat.',
    gradient: 'from-sky-400 to-blue-500',
    glow: 'rgba(56,189,248,0.25)',
    tag: 'Klagobot',
    tagColor: 'text-sky-700 bg-sky-50',
  },
];

const CustomersPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>Kunder & Case Studies — Aifinity AB</title>
        <meta name="description" content="Företag som valde att jobba smartare. Se hur Aifinity automatiserat däckbranschen, HR-flöden och mer — med mätbara resultat." />
        <meta property="og:url" content="https://aifinity.se/exemple" />
        <link rel="canonical" href="https://aifinity.se/exemple" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="Exempel på lösningar"
        heading={<>Vad kan vi<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>lösa åt er?</span></>}
        subtext="Konkreta exempel på hur Aifinity hjälper verksamheter spara tid och förenkla vardagen — utan långa projekt."
      />

      {/* ── Scenario blocks ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Scenarion</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Känner du igen dig?
            </h2>
            <p className="text-text-secondary mt-4 max-w-xl mx-auto leading-relaxed">
              Nedan ser du typiska situationer vi löser — med kort beskrivning av problem, lösning och förväntad effekt.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.category}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-border-light p-7 transition-all duration-300"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 36px ${s.glow}, 0 4px 16px rgba(0,0,0,0.05)`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)'; }}
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-5">
                  <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${s.gradient} items-center justify-center shrink-0 shadow-md`}
                    style={{ boxShadow: `0 4px 12px ${s.glow}` }}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mb-1 ${s.tagColor}`}>
                      {s.tag}
                    </span>
                    <h3 className="text-[17px] font-bold text-text-primary leading-snug">{s.category}</h3>
                  </div>
                </div>

                {/* Problem */}
                <div className="flex gap-3 mb-3">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-red-400 mb-0.5">Problem</p>
                    <p className="text-[14px] text-text-secondary leading-relaxed">{s.problem}</p>
                  </div>
                </div>

                {/* Solution */}
                <div className="flex gap-3 mb-3">
                  <Lightbulb className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-indigo mb-0.5">Lösning</p>
                    <p className="text-[14px] text-text-secondary leading-relaxed">{s.solution}</p>
                  </div>
                </div>

                {/* Effect */}
                <div className="flex gap-3">
                  <TrendingUp className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600 mb-0.5">Förväntad effekt</p>
                    <p className="text-[14px] text-text-secondary leading-relaxed">{s.effect}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Passar något av detta in på er?
            </h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto leading-relaxed">
              Berätta vad som tar onödig tid på ert företag. Det kostar ingenting att prata.
            </p>
            <Link to="/contact">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
                Skicka förfrågan
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
            <p className="text-xs text-text-muted mt-3">Svar inom 24 timmar · Du får ett konkret upplägg</p>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default CustomersPage;
